import React, { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import FormTextField from './FormTextField';

import ButtonContainer from './ButtonContainer';
import TallButton from './TallButton';
import messages from './OtpCodeField.messages';

import { OtpCodeFieldError } from '../types';
import { usePrevious } from '../utilities/hooks';

export type CodeType = 'numeric' | 'alphanumeric';
type CodeLength = 6 | 8 | undefined;

export interface OtpCodeFieldProps {
  id: string;
  disabled: boolean;
  fieldErrorFromParent?: OtpCodeFieldError;
  onChange?: (object: { target: HTMLInputElement | null | undefined }) => void;
  codeLength?: CodeLength;
  codeType: CodeType;
  clearInputTrigger?: number;
}

const getAcceptableCharsRegex = codeType => {
  switch (codeType) {
    case 'numeric':
      return '\\d';
    case 'alphanumeric':
      return '[A-Za-z\\d]';
    default:
      return '.';
  }
};

const sanitizeInput = (input: string, acceptableCharsRegex: string) => {
  return input.match(new RegExp(acceptableCharsRegex, 'g'))?.join('') || '';
};

const getInputValidation = (
  codeType: CodeType,
  codeLength: CodeLength,
  acceptableCharsRegex: string,
  formatMessage: IntlShape['formatMessage']
) => {
  if (codeLength === undefined) {
    return {
      label: formatMessage(messages.mfFormLabel),
      pattern: `${acceptableCharsRegex}+`,
      maxLength: undefined,
    };
  } else {
    const descriptor =
      codeType == 'numeric' ? messages.mfFormLabelNDigits : messages.mfFormLabelNCharacters;
    return {
      label: formatMessage(descriptor, { codeLength }),
      pattern: `${acceptableCharsRegex}{${codeLength}}`,
      maxLength: codeLength,
    };
  }
};

const OtpCodeField = ({
  codeLength,
  codeType,
  disabled,
  fieldErrorFromParent,
  id,
  onChange,
  clearInputTrigger,
}: OtpCodeFieldProps) => {
  const { formatMessage } = useIntl();

  const fieldTextRef = useRef<HTMLInputElement>(null);
  const [fieldValue, setFieldValue] = useState('');
  const [fieldError, setFieldError] = useState<OtpCodeFieldError | undefined>(fieldErrorFromParent);
  const previousFieldErrorFromParent = usePrevious(fieldErrorFromParent);
  const previousFieldValue = usePrevious(fieldValue);
  const previousClearInputTrigger = usePrevious(clearInputTrigger);
  const acceptableCharsRegex = getAcceptableCharsRegex(codeType);
  const { maxLength, pattern, label } = getInputValidation(
    codeType,
    codeLength,
    acceptableCharsRegex,
    formatMessage
  );

  useEffect(() => {
    if (!previousFieldErrorFromParent && fieldErrorFromParent) {
      setFieldError(fieldErrorFromParent);
      fieldTextRef.current && fieldTextRef.current.select();
    }
  }, [fieldErrorFromParent, previousFieldErrorFromParent]);

  useEffect(() => {
    if (clearInputTrigger && clearInputTrigger !== previousClearInputTrigger) {
      setFieldValue('');
      setFieldError(undefined);
    }
  }, [clearInputTrigger, previousClearInputTrigger]);

  useEffect(() => {
    if (fieldValue !== previousFieldValue) {
      onChange &&
        onChange({
          target: fieldTextRef.current,
        });
    }
  }, [fieldValue, previousFieldValue, onChange]);

  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      const target: HTMLInputElement = e.target as HTMLInputElement;
      const selectionStart = target.selectionStart || 0;
      const selectionEnd = target.selectionEnd || target.value.length;
      const start = target.value.substr(0, selectionStart);
      const clipboardData = (e.clipboardData || window.clipboardData).getData('text/plain');
      const sanitized = sanitizeInput(clipboardData, acceptableCharsRegex);
      const middle =
        codeLength === undefined
          ? sanitized
          : sanitized.substr(
              0,
              target.maxLength - target.value.length - selectionStart + selectionEnd
            );
      const end = target.value.substr(selectionEnd);
      //timeout to allow input update UI
      setTimeout(() => setFieldValue(start + middle + end));
    },
    [codeLength, acceptableCharsRegex]
  );

  const handleChange = useCallback(
    (e: SyntheticEvent<HTMLInputElement>) => {
      setFieldValue(sanitizeInput(e.currentTarget.value, acceptableCharsRegex));
      setFieldError(undefined);
    },
    [acceptableCharsRegex]
  );

  const handleInvalid = useCallback((e: Event) => {
    e.preventDefault();
    setFieldError({ errorCode: 'invalid_otp_pattern' });
  }, []);

  useEffect(() => {
    const input = fieldTextRef.current;
    if (input) {
      input.addEventListener('invalid', handleInvalid);
      input.addEventListener('paste', handlePaste);
    }
    return () => {
      if (input) {
        input.removeEventListener('invalid', handleInvalid);
        input.removeEventListener('paste', handlePaste);
      }
    };
  }, [handleInvalid, handlePaste]);

  const errorContent = (error: OtpCodeFieldError) => {
    switch (error && error.errorCode) {
      case 'invalid_otp':
        return formatMessage(messages.errorsInvalidOtp);
      case 'invalid_otp_format':
        return formatMessage(messages.errorsInvalidOtpFormat);
      case 'invalid_otp_pattern':
        return formatMessage(messages.errorsInvalidOtpPattern, { codeLength });
      case 'expired_otp':
        return formatMessage(messages.errorsExpiredOtp);
      case 'too_many_requests':
        return formatMessage(messages.errorsInvalidOtp);
      case 'unknown':
        return formatMessage(messages.errorsUnknown);
      case 'invalid_token':
        return (
          <form onSubmit={() => window.location.reload()}>
            <FormattedMessage
              id="mf.mfform.errors.invalid_token"
              defaultMessage="Your session has expired. Please refresh the page and try again."
            />
            <ButtonContainer>
              <TallButton type="submit" isFullWidth>
                <FormattedMessage id="common.refresh" defaultMessage="Refresh" />
              </TallButton>
            </ButtonContainer>
          </form>
        );
      default:
        return undefined;
    }
  };

  return (
    <FormTextField
      id={id}
      name="otpCode"
      type={codeType === 'numeric' ? 'tel' : 'text'}
      label={label}
      autoFocus
      maxLength={maxLength}
      pattern={pattern}
      onChange={handleChange}
      isInvalid={!!fieldError}
      invalidMessage={!disabled && fieldError && errorContent(fieldError)}
      value={fieldValue}
      isDisabled={disabled}
      isRequired
      ref={fieldTextRef}
    />
  );
};

export default OtpCodeField;
